/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

const LOCAL_STORAGE_HISTORY_KEY = 'history.nodals.ai';
const PRUNE_THRESHOLD_DAYS = 30;

interface HierarchyNode {
  id: string;
  children: HierarchyNode[];
}

class ViewManager {
  public static recordView(adId: string): void {
    const data = this.getData();
    const now = Date.now();

    if (!data[adId]) {
      data[adId] = [];
    }

    data[adId].push(now);
    this.save(data);
  }

  public static getViewsForEntity(entityId: string, hierarchy: HierarchyNode): number[] {
    const data = this.getData();
    const [cleanedData, hasChanges] = this.prune(data);

    if (hasChanges) {
      this.save(cleanedData);
    }

    const adIds = ViewManager.getAdIdsForEntity(entityId, hierarchy);
    return adIds.flatMap((adId) => cleanedData[adId] ?? []);
  }

  private static getAdIdsForEntity(entityId: string, root: HierarchyNode): string[] {
    const targetNode = ViewManager.findNodeById(root, entityId);
    if (!targetNode) return [];

    const adIds: string[] = [];
    ViewManager.collectLeafIds(targetNode, adIds);
    return adIds;
  }

  private static findNodeById(node: HierarchyNode, id: string): HierarchyNode | null {
    if (node.id === id) return node;

    for (const child of node.children) {
      const result = ViewManager.findNodeById(child, id);
      if (result) return result;
    }

    return null;
  }

  private static collectLeafIds(node: HierarchyNode, adIds: string[]): void {
    if (node.children.length === 0) {
      adIds.push(node.id);
    } else {
      for (const child of node.children) {
        ViewManager.collectLeafIds(child, adIds);
      }
    }
  }

  private static getData(): Record<string, number[]> {
    const raw = localStorage.getItem(LOCAL_STORAGE_HISTORY_KEY);
    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) {
        return {};
      }

      const validData: Record<string, number[]> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (Array.isArray(value) && value.every((item) => typeof item === 'number')) {
          validData[key] = value;
        }
      }

      return validData;
    } catch {
      return {};
    }
  }

  private static save(data: Record<string, number[]>): void {
    localStorage.setItem(LOCAL_STORAGE_HISTORY_KEY, JSON.stringify(data));
  }

  private static prune(data: Record<string, number[]>): [Record<string, number[]>, boolean] {
    const cutoff = Date.now() - PRUNE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    let hasChanges = false;

    const cleaned: Record<string, number[]> = {};

    for (const [adId, timestamps] of Object.entries(data)) {
      const pruned = timestamps.filter((ts) => ts >= cutoff);
      if (pruned.length > 0) {
        cleaned[adId] = pruned;
        if (pruned.length !== timestamps.length) {
          hasChanges = true;
        }
      } else {
        hasChanges = true;
      }
    }

    return [cleaned, hasChanges];
  }
}

export { ViewManager };
export type { HierarchyNode };
